import { useState, useTransition, type JSX } from "react";

import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    Button,
    CollapseContentSection,
    CollapseTransition,
} from "@impero-com/ui";
import { Checkbox, Switch, Table } from "antd";
import { Control } from "react-hook-form";

import {
    DefaultOrCustomForDueDateOffsetForMonthOffsetPeriod as DefaultOrCustomOffset,
    DueDateOffsetForMonthOffsetPeriod as MonthDueDateOffset,
    ControlRecurrenceRule,
    CalendarView,
} from "api/generated/model";
import { formatControlSchedulingType } from "org/control/utils";

import {
    translateHalfYear,
    translateMonth,
    translateQuarter,
    translateTwoMonthsPeriod,
} from "../../list/utils";
import {
    ALL_HALF_YEARS,
    ALL_MONTHS,
    ALL_QUARTERS,
    ALL_TWO_MONTHS,
} from "../consts";
import { ControlViewFormSchema } from "../formSchema";

import { CustomMonthDueDateOffsetInput } from "./MonthDueDateOffsetInput";
import * as theme from "./OccurrenceTable.theme.css";

export interface OccurrenceTableProps {
    control: Control<ControlViewFormSchema>;
    value: OccurrenceEntry[];
    onChange: (entries: OccurrenceEntry[]) => void;
    defaultDueDateOffset: MonthDueDateOffset;
    calendarId: number | null;
    recurrenceType: OccurrenceType;
    initialCalendars: CalendarView[];
    disabled?: boolean;
}

export type RuleWithOccurrence = Extract<
    ControlRecurrenceRule,
    { occurrences: object }
>;
export type OccurrenceType = RuleWithOccurrence["type"];
export interface OccurrenceEntry {
    period: string;
    dueDateOffset: MonthDueDateOffset | "ABSENT" | "DEFAULT";
}

export const OccurrenceTable = ({
    control,
    value: entries,
    onChange,
    defaultDueDateOffset,
    calendarId,
    recurrenceType,
    initialCalendars,
    disabled,
}: OccurrenceTableProps) => {
    const { t } = useTranslation();

    const hasAtLeastOne = entries.some((e) => e.dueDateOffset !== "ABSENT");

    const translatePeriod = {
        Monthly: translateMonth,
        EveryTwoMonths: translateTwoMonthsPeriod,
        Quarterly: translateQuarter,
        HalfYearly: translateHalfYear,
    }[recurrenceType] as (period: string) => string;

    const initialCollapsed = entries.every(
        (e) => e.dueDateOffset === "DEFAULT"
    );
    const [tableCollapsed, setTableCollapsed] =
        useState<boolean>(initialCollapsed);
    const [isCollapseTransitioning, startCollapseTransition] = useTransition();

    const handleCheckboxChange = () => {
        startCollapseTransition(() => {
            setTableCollapsed(!tableCollapsed);
        });
        if (!tableCollapsed) {
            // Reset on uncheck: set all to DEFAULT
            onChange(entries.map((e) => ({ ...e, dueDateOffset: "DEFAULT" })));
        }
    };

    const displayChecked =
        isCollapseTransitioning && !tableCollapsed ? false : !tableCollapsed;

    const updateEntry = (
        period: string,
        updater: (entry: OccurrenceEntry) => OccurrenceEntry
    ) => {
        onChange(entries.map((e) => (e.period === period ? updater(e) : e)));
    };

    return (
        <div className={theme.occurrence}>
            <div>
                <Checkbox
                    checked={displayChecked}
                    onClick={handleCheckboxChange}
                    disabled={disabled}
                >
                    {t(
                        "CreateControl_Recurrence_IntervalDetails_CustomizeSchedule",
                        {
                            interval: formatControlSchedulingType(
                                recurrenceType,
                                t
                            ).toLowerCase(),
                        }
                    )}
                </Checkbox>
            </div>
            <CollapseTransition collapsed={tableCollapsed}>
                <div className={theme.occurrenceTableSpace} />
                <CollapseContentSection
                    level={5}
                    styleVariant="secondary"
                    noPadding
                    titleLabel={t(
                        "CreateControl_Recurrence_IntervalDetails_CustomizeScheduleCollapse"
                    )}
                >
                    <Table
                        dataSource={entries}
                        rowKey={(entry) => entry.period}
                        columns={[
                            {
                                title: t("Control_Scheduling_Send_Title"),
                                key: "isPresent",
                                width: 90,
                                render: (_, entry) => (
                                    <div className={theme.sendOutColItem}>
                                        <OccurrenceIsPresent
                                            entry={entry}
                                            onChange={(checked) =>
                                                updateEntry(
                                                    entry.period,
                                                    (e) => ({
                                                        ...e,
                                                        dueDateOffset: checked
                                                            ? "DEFAULT"
                                                            : "ABSENT",
                                                    })
                                                )
                                            }
                                            disabled={disabled}
                                        />
                                    </div>
                                ),
                            },
                            {
                                title: periodTitle(recurrenceType, t),
                                key: "period",
                                width: 100,
                                render: (_, entry) =>
                                    translatePeriod(entry.period),
                                className: theme.nowrap,
                            },
                            {
                                title: t(
                                    "Control_Scheduling_CustomDueDate_Title"
                                ),
                                key: "customDueDate",
                                render: (_, entry) => (
                                    <OccurrenceCustomDueDate
                                        control={control}
                                        entry={entry}
                                        onEntryChange={(updated) =>
                                            updateEntry(
                                                entry.period,
                                                () => updated
                                            )
                                        }
                                        defaultDueDateOffset={
                                            defaultDueDateOffset
                                        }
                                        calendarId={calendarId}
                                        recurrenceType={recurrenceType}
                                        initialCalendars={initialCalendars}
                                        disabled={disabled}
                                    />
                                ),
                            },
                            {
                                title: t("Control_Scheduling_Action_Title"),
                                key: "action",
                                width: 100,
                                render: (_, entry) => (
                                    <OccurrenceReset
                                        entry={entry}
                                        onReset={() =>
                                            updateEntry(entry.period, (e) => ({
                                                ...e,
                                                dueDateOffset: "DEFAULT",
                                            }))
                                        }
                                        disabled={disabled}
                                    />
                                ),
                            },
                        ]}
                        pagination={false}
                    />
                </CollapseContentSection>
            </CollapseTransition>
            {!hasAtLeastOne && (
                <div className={theme.occurrenceError}>
                    {t("Recurrence_Validation_Invalid_Occurrences")}
                </div>
            )}
        </div>
    );
};

function periodTitle(recurrenceType: OccurrenceType, t: TFunction): string {
    return {
        Monthly: t("Control_Scheduling_Period_Monthly_Title"),
        EveryTwoMonths: t("Control_Scheduling_Period_EveryTwoMonths_Title"),
        Quarterly: t("Control_Scheduling_Period_Quarterly_Title"),
        HalfYearly: t("Control_Scheduling_Period_HalfYearly_Title"),
    }[recurrenceType];
}

function allOccurrencePeriodKeys(type: OccurrenceType) {
    switch (type) {
        case "Monthly":
            return ALL_MONTHS;
        case "EveryTwoMonths":
            return ALL_TWO_MONTHS;
        case "Quarterly":
            return ALL_QUARTERS;
        case "HalfYearly":
            return ALL_HALF_YEARS;
        default:
            assertNever(type);
    }
}

export function toOccurrenceEntries(
    rule: RuleWithOccurrence
): OccurrenceEntry[] {
    const occurrences = rule.occurrences as Record<
        string,
        DefaultOrCustomOffset
    >;
    return allOccurrencePeriodKeys(rule.type).map((period) => {
        const occurrence = occurrences[period];
        let dueDateOffset: OccurrenceEntry["dueDateOffset"];
        switch (occurrence) {
            case "Default":
                dueDateOffset = "DEFAULT";
                break;
            case undefined:
                dueDateOffset = "ABSENT";
                break;
            default:
                dueDateOffset = occurrence.Custom;
        }
        return { period, dueDateOffset };
    });
}

export function updateOccurrenceEntries(
    rule: RuleWithOccurrence,
    occurrenceEntries: OccurrenceEntry[]
): RuleWithOccurrence {
    const occurrences: Record<string, DefaultOrCustomOffset> = {};
    for (const entry of occurrenceEntries) {
        if (entry.dueDateOffset !== "ABSENT") {
            occurrences[entry.period] =
                entry.dueDateOffset === "DEFAULT"
                    ? "Default"
                    : { Custom: entry.dueDateOffset };
        }
    }
    return {
        ...rule,
        occurrences,
    };
}

function isMonthDueDateOffset(
    dueDateOffset: OccurrenceEntry["dueDateOffset"]
): dueDateOffset is MonthDueDateOffset {
    return dueDateOffset !== "ABSENT" && dueDateOffset !== "DEFAULT";
}

function isRuleWithOccurrence(
    rule: ControlRecurrenceRule
): rule is RuleWithOccurrence {
    return Object.hasOwn(rule, "occurrences");
}

// --- Sub-components ---

interface OccurrenceIsPresentProps {
    entry: OccurrenceEntry;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
}

const OccurrenceIsPresent = ({
    entry,
    onChange,
    disabled,
}: OccurrenceIsPresentProps) => {
    const isPresent = entry.dueDateOffset !== "ABSENT";
    return (
        <Switch onChange={onChange} checked={isPresent} disabled={disabled} />
    );
};

interface OccurrenceCustomDueDateProps {
    control: Control<ControlViewFormSchema>;
    entry: OccurrenceEntry;
    onEntryChange: (entry: OccurrenceEntry) => void;
    defaultDueDateOffset: MonthDueDateOffset;
    calendarId: number | null;
    recurrenceType: OccurrenceType;
    initialCalendars: CalendarView[];
    disabled?: boolean;
}

const OccurrenceCustomDueDate = ({
    control,
    entry,
    onEntryChange,
    defaultDueDateOffset,
    calendarId,
    recurrenceType,
    initialCalendars,
    disabled,
}: OccurrenceCustomDueDateProps): JSX.Element => {
    const { t } = useTranslation();

    if (isMonthDueDateOffset(entry.dueDateOffset)) {
        const handleOffsetChange = (offset: MonthDueDateOffset) => {
            onEntryChange({ ...entry, dueDateOffset: offset });
        };

        return (
            <CustomMonthDueDateOffsetInput
                control={control}
                value={entry.dueDateOffset}
                onChange={handleOffsetChange}
                calendarId={calendarId}
                recurrenceType={recurrenceType}
                initialCalendars={initialCalendars}
                disabled={disabled}
            />
        );
    }

    const isAbsent = entry.dueDateOffset === "ABSENT";
    const handleSetCustom = () => {
        onEntryChange({ ...entry, dueDateOffset: defaultDueDateOffset });
    };

    return (
        <Button
            id="setCustomDueDate"
            onClick={handleSetCustom}
            disabled={disabled || isAbsent}
            variant="link"
        >
            {isAbsent
                ? t("Control_Scheduling_NoSend")
                : t("Control_Scheduling_SetCustomDueDate")}
        </Button>
    );
};

interface OccurrenceResetProps {
    entry: OccurrenceEntry;
    onReset: () => void;
    disabled?: boolean;
}

const OccurrenceReset = ({
    entry,
    onReset,
    disabled,
}: OccurrenceResetProps) => {
    const { t } = useTranslation();

    const inactive =
        disabled ||
        entry.dueDateOffset === "DEFAULT" ||
        entry.dueDateOffset === "ABSENT";
    return (
        <Button
            id="occurrenceReset"
            onClick={onReset}
            disabled={inactive}
            variant="link"
            className={theme.nopadding}
        >
            {t("Control_Scheduling_Reset")}
        </Button>
    );
};

// Re-export for use in ControlSchedule
export { isRuleWithOccurrence };
